var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { withPathPrefix } from "../../common/helpers";
import { fetchFacets, fetchInitialPayload } from "../api/facets";
import { useListingLocation } from "../hooks/useListingLocation";
import { withLockedFinanceType, withoutLockedPathFilters, } from "../utils/financeTypeLock";
import { withHiddenFilters, withoutHiddenFilters, withoutHiddenSelection, } from "../utils/hiddenFilters";
import { parsePathFiltersFromPathname } from "../utils/parsePathFiltersFromPathname";
import { selectedStateFromPayload } from "../utils/selectedStateFromPayload";
import { useEffect, useRef } from "react";
const facetsUrl = withPathPrefix("/carsell/offers/facets");
/**
 * Wires up all async side-effects for the offer filters:
 *   1. Initial load — fetches filter config + facets on first mount.
 *   2. Facet refresh — re-fetches facets (counts) whenever the user changes a
 *      filter selection (driven by `pendingRefresh`).
 *   3. URL sync — re-syncs filter state after back/forward browser navigation
 *      or any external URL change (e.g. the PHP-side sort widget navigating).
 */
export function useOfferFiltersSync(state, dispatch) {
    const location = useListingLocation();
    const previousLocationRef = useRef(location);
    // 1. Initial load: fetch filter config, path filter definitions and initial
    //    facet counts from the server, then populate the reducer.
    useEffect(() => {
        if (!state.isInitialLoading || state.initialized)
            return;
        const locked = state.lockedFinanceType;
        const hidden = state.hiddenFilters;
        const load = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            try {
                const payload = yield fetchInitialPayload(facetsUrl, window.location.pathname, window.location.search, locked, hidden);
                if (!payload) {
                    dispatch({ type: "initialize_failure" });
                    return;
                }
                dispatch({
                    type: "initialize_success",
                    payload: {
                        filterConfig: withoutHiddenFilters((_a = payload.filterConfig) !== null && _a !== void 0 ? _a : [], hidden),
                        pathFilters: withoutHiddenFilters(withoutLockedPathFilters((_b = payload.pathFilters) !== null && _b !== void 0 ? _b : [], locked), hidden),
                        selectedState: withoutHiddenSelection(withLockedFinanceType(selectedStateFromPayload(payload), locked), hidden),
                        facetData: (_c = payload.facets) !== null && _c !== void 0 ? _c : {},
                        offersCount: payload.offersCount,
                    },
                });
            }
            catch (_d) {
                dispatch({ type: "initialize_failure" });
            }
        });
        void load();
    }, [
        state.isInitialLoading,
        state.initialized,
        state.lockedFinanceType,
        state.hiddenFilters,
        dispatch,
    ]);
    // 2. Facet refresh: when the user changes a filter, re-fetch facet counts so
    //    the UI reflects how many offers each option would return.
    useEffect(() => {
        if (!state.pendingRefresh)
            return;
        const refresh = () => __awaiter(this, void 0, void 0, function* () {
            dispatch({ type: "facets_request" });
            try {
                const response = yield fetchFacets(facetsUrl, withHiddenFilters(state.selectedState, state.hiddenFilters));
                if (!response)
                    return;
                dispatch({
                    type: "facets_success",
                    payload: {
                        facets: response.facets,
                        offersCount: response.offersCount,
                    },
                });
            }
            catch (_a) {
                dispatch({ type: "facets_failure" });
            }
        });
        void refresh();
    }, [
        state.pendingRefresh,
        state.selectedState,
        state.hiddenFilters,
        dispatch,
    ]);
    // 3. URL sync: after back/forward navigation (or an external pushState, e.g.
    //    the PHP sort widget), re-parse the new URL and re-fetch facets so filter
    //    selections and counts stay consistent with what's shown in the listing.
    useEffect(() => {
        if (!state.initialized || previousLocationRef.current === location) {
            previousLocationRef.current = location;
            return;
        }
        previousLocationRef.current = location;
        const pathState = parsePathFiltersFromPathname(location.pathname);
        if (Object.keys(pathState).length > 0) {
            dispatch({ type: "apply_path_from_url", payload: pathState });
        }
        const sync = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const payload = yield fetchInitialPayload(facetsUrl, location.pathname, location.search, state.lockedFinanceType, state.hiddenFilters);
                if (!payload)
                    return;
                dispatch({
                    type: "sync_from_url_success",
                    payload: {
                        selectedState: withoutHiddenSelection(withLockedFinanceType(selectedStateFromPayload(payload, location.pathname, location.search), state.lockedFinanceType), state.hiddenFilters),
                        facetData: (_a = payload.facets) !== null && _a !== void 0 ? _a : {},
                        offersCount: payload.offersCount,
                    },
                });
            }
            catch (_b) {
                dispatch({ type: "facets_failure" });
            }
        });
        void sync();
    }, [
        state.initialized,
        state.lockedFinanceType,
        state.hiddenFilters,
        location,
        dispatch,
    ]);
}
